import { Add } from "@mui/icons-material";
import { Fab, TextField } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";
import { parseAsString } from "nuqs";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";

import GaiaDataList from "~/modules/app/components/GaiaDataList";
import GaiaPageGridComponent from "~/modules/app/components/GaiaPageGridComponent";
import GaiaPageLayoutColumn from "~/modules/app/components/GaiaPageLayoutColumn";
import GaiaTableLinkCell from "~/modules/app/components/GaiaTableLinkCell";
import ErrorScreen from "~/modules/app/screens/ErrorScreen";
import { useDebouncedValue } from "~/shared/hooks/useDebouncedValue";
import { useGridQueryState } from "~/shared/hooks/useGridQueryState";

import useClientListScreen_GetAll from "./api/useClientListScreen_GetAll";

export default function ClientListScreen() {
    const { t } = useTranslation("client");

    const [queryState, setQueryState, gridModelProps] = useGridQueryState({
        q: parseAsString.withDefault(""),
    });

    const debouncedQ = useDebouncedValue(queryState.q);

    const { data, loading, error, totalRows } = useClientListScreen_GetAll({
        ...queryState,
        q: debouncedQ || undefined,
    });

    const columns: GridColDef[] = useMemo(
        () => [
            {
                field: "name",
                headerName: t("clientListScreen.colName"),
                flex: 1,
            },
            {
                field: "type",
                headerName: t("clientListScreen.colType"),
                flex: 1,
                renderCell: ({ value }) => t(`clientForm.typeValue.${value}`),
            },
            {
                type: "actions",
                field: "actions",
                valueGetter: (_, row) => `/c/${row.slug}`,
                headerName: "",
                maxWidth: 40,
                renderCell: props => <GaiaTableLinkCell value={props.value} />,
            },
        ],
        [t],
    );

    if (error) {
        return <ErrorScreen error={error} />;
    }

    return (
        <GaiaPageLayoutColumn maxWidth="xl">
            <GaiaPageGridComponent
                title={t("clientListScreen.title")}
                actions={
                    <Fab component={Link} to="new" color="primary">
                        <Add />
                    </Fab>
                }
            >
                <TextField
                    label={t("clientListScreen.filterName")}
                    variant="outlined"
                    value={queryState.q}
                    onChange={e => setQueryState({ q: e.target.value, page: 0 })}
                    sx={{ width: "25%" }}
                />
                <GaiaDataList
                    data={data || []}
                    loading={loading}
                    error={error}
                    totalRows={totalRows || 0}
                    columns={columns}
                    gridModels={gridModelProps}
                />
            </GaiaPageGridComponent>
        </GaiaPageLayoutColumn>
    );
}
